import { Box } from "@mui/material";
import { FC, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import dayjs from "dayjs";

interface SmallLineChartPoint {
  date: string;
  value: number;
  data?: any;
}

interface SmallLineChartSeries {
  name: string;
  data: SmallLineChartPoint[];
  color?: string;
}

interface SmallLineChartProps {
  // Single-series convenience API (existing usage)
  data?: SmallLineChartPoint[];
  // Multi-series API (e.g. learner vs coach)
  series?: SmallLineChartSeries[];
  height?: number;
  onClickDot?: (data: any) => void;
}

export const SmallLineChart: FC<SmallLineChartProps> = ({
  data,
  series,
  height = 150,
  onClickDot,
}) => {
  const theme = useTheme();

  const chartOptions = useMemo(() => {
    // Normalize input into Highcharts series
    const buildSeriesFromPoints = (
      name: string,
      color: string | undefined,
      points: SmallLineChartPoint[],
    ) =>
      points.map((item) => ({
        x: dayjs(item.date).valueOf(),
        y: item.value,
        custom: item.data,
      }));

    const hasMultiSeries = !!series && series.length > 0;

    const chartSeries = hasMultiSeries
      ? series!.map((s) => ({
          type: "line",
          name: s.name,
          data: buildSeriesFromPoints(
            s.name,
            s.color,
            s.data || [],
          ) as any[],
          color: s.color,
          showInLegend: true,
        }))
      : [
          {
            type: "line",
            name: "Value",
            data: buildSeriesFromPoints(
              "Value",
              theme.palette.primary.main,
              data || [],
            ) as any[],
            color: theme.palette.primary.main,
            showInLegend: false,
          },
        ];

    return {
      time: {
        useUTC: false,
      },
      chart: {
        type: "line",
        height: height,
        backgroundColor: "transparent",
        margin: [10, 10, 30, 50],
        spacing: [0, 0, 0, 0],
      },
      title: {
        text: null,
      },
      xAxis: {
        type: "datetime",
        lineColor: theme.palette.divider,
        tickColor: theme.palette.divider,
        labels: {
          style: {
            color: theme.palette.text.secondary,
            fontSize: "12px",
          },
          formatter: function (this: any): string {
            return dayjs(this.value).format("MMM DD");
          },
        },
        gridLineColor: theme.palette.divider,
        gridLineWidth: 0,
      },
      yAxis: {
        title: {
          text: null,
        },
        lineColor: theme.palette.divider,
        tickColor: theme.palette.divider,
        labels: {
          style: {
            color: theme.palette.text.secondary,
            fontSize: "12px",
          },
        },
        gridLineColor: theme.palette.divider,
        gridLineWidth: 1,
        gridLineDashStyle: "Dot",
      },
      plotOptions: {
        line: {
          marker: {
            enabled: true,
            radius: 3,
            lineWidth: 2,
          },
          lineWidth: 2,
          states: {
            hover: {
              lineWidth: 3,
            },
          },
          enableMouseTracking: true,
        },
        series: {
          cursor: onClickDot ? "pointer" : "default",
          point: {
            events: {
              click: function (this: Highcharts.Point) {
                if (onClickDot) {
                  // @ts-ignore Highcharts typing allows arbitrary fields on options
                  onClickDot(this.options.custom);
                }
              },
            },
          },
        },
      },
      series: chartSeries,
      credits: {
        enabled: false,
      },
      legend: {
        enabled: hasMultiSeries,
        align: "right",
        verticalAlign: "top",
        layout: "horizontal",
        itemStyle: {
          fontSize: "12px",
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        borderRadius: 4,
        shadow: true,
        style: {
          color: theme.palette.text.primary,
        },
        shared: hasMultiSeries,
        formatter: function (this: any): string {
          if (hasMultiSeries && this.points) {
            const lines = this.points
              .map(
                (p: any) =>
                  `<div>${p.series.name}: <strong>${p.y}</strong></div>`,
              )
              .join("");
            return `
              <div style="padding: 8px;">
                <div style="margin-bottom: 4px; font-weight: 600;">
                  ${dayjs(this.x).format("MMMM DD, YYYY")}
                </div>
                ${lines}
              </div>
            `;
          }

          return `
            <div style="padding: 8px;">
              <div style="margin-bottom: 4px; font-weight: 600;">
                ${dayjs(this.x).format("MMMM DD, YYYY")}
              </div>
              <div>Value: <strong>${this.y}</strong></div>
            </div>
          `;
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              yAxis: {
                labels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
              xAxis: {
                labels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            },
          },
        ],
      },
    };
  }, [data, series, height, theme, onClickDot]);

  const hasData =
    (series && series.some((s) => s.data && s.data.length > 0)) ||
    (data && data.length > 0);

  if (!hasData) {
    return (
      <Box
        sx={{
          height: height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        No data available
      </Box>
    );
  }

  return (
    <Box sx={{ height: height, width: "100%" }}>
      <HighchartsReact
        containerProps={{ style: { height: "100%", width: "100%" } }}
        highcharts={Highcharts}
        options={chartOptions}
      />
    </Box>
  );
};
